import { IonIcon } from "@ionic/react";
import { calendarOutline, navigateCircleOutline, timeOutline } from "ionicons/icons";
import { LayoutGroup, motion } from "motion/react";
import React, { useState } from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { isPurchasedItineraryEntry } from "../../../tools/purchasedItineraries.tools";
import {
    resolveItineraryDateLabel,
    resolveSavedItineraryDestinationLabel,
    resolveSavedItineraryOriginLabel,
    type SavedListEntry,
} from "../../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";
import SectionLabel from "../../shared/SectionLabel";
import {
    buildSavedItineraryListItemLayoutTransition,
    resolveSavedItineraryListItemAnimationProfile,
} from "../savedItinerary/savedItineraryList.animation.tools";
import SavedItineraryListItemMotion from "../savedItinerary/SavedItineraryListItemMotion";
import SavedItineraryListPresence from "../savedItinerary/SavedItineraryListPresence";
import ItineraryItem from "./ItineraryItem";

interface SavedItineraryAccordionListProps {

    /** Saved entries in the past temporal bucket. */
    "pastEntries": SavedListEntry[];

    /** Saved entries in the current / in-progress temporal bucket. */
    "currentEntries": SavedListEntry[];

    /** Saved entries in the upcoming temporal bucket. */
    "upcomingEntries": SavedListEntry[];

    /** Enables dark palette variants for section labels and cards. */
    "isDarkUi": boolean;

    /** Whether list prices should render in EUR (unused when price is hidden). */
    "isEuro": boolean;

    /** Pricing preferences forwarded to itinerary row chrome. */
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** Opens itinerary details for the selected saved itinerary. */
    "onItineraryClick": ( itinerary: ItineraryDTO ) => void;

    /** Removes one saved itinerary from persistence. */
    "onRemoveSavedItinerary": ( itineraryId: string ) => void;

    /** Called when a presence exit animation finishes (last-item shell cleanup). */
    "onSavedListExitComplete": () => void;
}

/**
 * Temporal accordion for the saved-itinerary list (past / current / upcoming).
 *
 * @remarks Owns section expansion state. Past starts collapsed; current and upcoming start open.
 *
 * @param props Section entries and interaction callbacks.
 * @returns Scrollable saved-list accordion.
 */
const SavedItineraryAccordionList: React.FC<SavedItineraryAccordionListProps> = ( {
    pastEntries,
    currentEntries,
    upcomingEntries,
    isDarkUi,
    isEuro,
    pricingPreferences,
    onItineraryClick,
    onRemoveSavedItinerary,
    onSavedListExitComplete,
} ) => {
    const reducedMotion = useReducedMotion();
    const [ isPastSectionExpanded, setIsPastSectionExpanded ] = useState( false );
    const [ isCurrentSectionExpanded, setIsCurrentSectionExpanded ] = useState( true );
    const [ isUpcomingSectionExpanded, setIsUpcomingSectionExpanded ] = useState( true );

    /**
     * Renders one saved-itinerary row with motion chrome and list header actions.
     *
     * @param savedEntry Persisted saved entry to display.
     * @param index Zero-based index in the section list (drives stagger).
     * @param itemKeyStablePrefix Prefix for fallback keys when itinerary id is missing.
     * @returns Animated saved itinerary list item.
     */
    const renderSavedItineraryListItem = (
        savedEntry: SavedListEntry,
        index: number,
        itemKeyStablePrefix: string,
    ): React.ReactElement => {
        const itinerary = savedEntry.itinerary;
        const animationProfile = resolveSavedItineraryListItemAnimationProfile();
        const isPurchased = isPurchasedItineraryEntry( savedEntry );

        // Purchased keys are prefixed: one itinerary can be both a favorite and a purchase.
        const itemKey = isPurchased
            ? `purchased-${ savedEntry.purchase.providerBookingId }`
            : itinerary.id ?? `${ itemKeyStablePrefix }-${ index }`;

        return (
            <SavedItineraryListItemMotion
                animationProfile={animationProfile}
                index={index}
                key={itemKey}
            >
                <ItineraryItem
                    handleItineraryClick={onItineraryClick}
                    index={index}
                    isDarkUi={isDarkUi}
                    isEuro={isEuro}
                    isPriceLoading={false}
                    itinerary={itinerary}
                    pricingPreferences={pricingPreferences}
                    savedListHeader={{
                        "destinationLabel": resolveSavedItineraryDestinationLabel( savedEntry ),
                        "displayDate":      resolveItineraryDateLabel( itinerary ),
                        "isPurchased":      isPurchased,
                        "itineraryDomId":   itemKey,
                        ...isPurchased
                            ? {}
                            : {
                                "onRemove": () => {
                                    if ( itinerary.id != null ) {
                                        onRemoveSavedItinerary( itinerary.id );
                                    }
                                },
                            },
                        "originLabel": resolveSavedItineraryOriginLabel( savedEntry ),
                    }}
                    showPrice={false}
                />
            </SavedItineraryListItemMotion>
        );
    };

    /**
     * Renders a temporal section accordion title with layout projection.
     *
     * @remarks Titles stay mounted while the section has entries. `layout="position"`
     * lets them slide when a section above opens or closes (instead of teleporting).
     *
     * @param options Section accordion label options.
     * @returns Animated section accordion header.
     */
    const renderSavedSectionLabel = ( options: {
        "domId":      string;
        "label":      string;
        "icon":       React.ReactElement;
        "isExpanded": boolean;
        "itemCount":  number;
        "onToggle":   () => void;
    } ): React.ReactElement => {
        const animationsEnabled = isUiMotionEnabled( reducedMotion );
        const layoutTransition = buildSavedItineraryListItemLayoutTransition( reducedMotion );

        if ( !animationsEnabled ) {
            return (
                <div className="mb-2 shrink-0">
                    <SectionLabel
                        headingLevel="h3"
                        icon={options.icon}
                        id={options.domId}
                        isDarkUi={isDarkUi}
                        isExpanded={options.isExpanded}
                        isFixedWidth={true}
                        itemCount={options.itemCount}
                        label={options.label}
                        onToggleExpand={options.onToggle}
                    />
                </div>
            );
        }

        return (
            <motion.div
                className="mb-2 shrink-0"
                layout="position"
                layoutScroll
                transition={{ "layout": layoutTransition }}
            >
                <SectionLabel
                    headingLevel="h3"
                    icon={options.icon}
                    id={options.domId}
                    isDarkUi={isDarkUi}
                    isExpanded={options.isExpanded}
                    isFixedWidth={true}
                    itemCount={options.itemCount}
                    label={options.label}
                    onToggleExpand={options.onToggle}
                />
            </motion.div>
        );
    };

    return (
        <div
            className="flowrideScrollBar min-h-0 flex-1 overflow-auto scroll-container [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            id="itinerary-list-scroll"
        >
            <div
                className="flex flex-col"
                id="itinerary-list-items"
            >
                <LayoutGroup id="saved-itinerary-list">
                    {pastEntries.length > 0
                        ? renderSavedSectionLabel( {
                            "domId": "saved-list-past-section-label",
                            "icon":  (
                                <IonIcon
                                    icon={timeOutline}
                                    style={{ "fontSize": "14px" }}
                                />
                            ),
                            "isExpanded": isPastSectionExpanded,
                            "itemCount":  pastEntries.length,
                            "label":      "Passé",
                            "onToggle":   () => {
                                setIsPastSectionExpanded( ( previous ) => !previous );
                            },
                        } )
                        : null}
                    <SavedItineraryListPresence onExitComplete={onSavedListExitComplete}>
                        {isPastSectionExpanded
                            ? pastEntries.map( ( savedEntry, index ) => renderSavedItineraryListItem( savedEntry, index, "saved-past" ) )
                            : null}
                    </SavedItineraryListPresence>
                    {currentEntries.length > 0
                        ? renderSavedSectionLabel( {
                            "domId": "saved-list-current-section-label",
                            "icon":  (
                                <IonIcon
                                    icon={navigateCircleOutline}
                                    style={{ "fontSize": "14px" }}
                                />
                            ),
                            "isExpanded": isCurrentSectionExpanded,
                            "itemCount":  currentEntries.length,
                            "label":      "En cours",
                            "onToggle":   () => {
                                setIsCurrentSectionExpanded( ( previous ) => !previous );
                            },
                        } )
                        : null}
                    <SavedItineraryListPresence onExitComplete={onSavedListExitComplete}>
                        {isCurrentSectionExpanded
                            ? currentEntries.map( ( savedEntry, index ) => renderSavedItineraryListItem(
                                savedEntry,
                                index,
                                "saved-current",
                            ) )
                            : null}
                    </SavedItineraryListPresence>
                    {upcomingEntries.length > 0
                        ? renderSavedSectionLabel( {
                            "domId": "saved-list-upcoming-section-label",
                            "icon":  (
                                <IonIcon
                                    icon={calendarOutline}
                                    style={{ "fontSize": "14px" }}
                                />
                            ),
                            "isExpanded": isUpcomingSectionExpanded,
                            "itemCount":  upcomingEntries.length,
                            "label":      "À venir",
                            "onToggle":   () => {
                                setIsUpcomingSectionExpanded( ( previous ) => !previous );
                            },
                        } )
                        : null}
                    <SavedItineraryListPresence onExitComplete={onSavedListExitComplete}>
                        {isUpcomingSectionExpanded
                            ? upcomingEntries.map( ( savedEntry, index ) => renderSavedItineraryListItem(
                                savedEntry,
                                index,
                                "saved-upcoming",
                            ) )
                            : null}
                    </SavedItineraryListPresence>
                </LayoutGroup>
            </div>
        </div>
    );
};

export default SavedItineraryAccordionList;
